import type {ApiResponse, PaginatedResult} from '@app/core/api';
import type {
  BulkPreviewModel,
  ContactDetailModel,
  ContactGroupModel,
  ContactListItemModel,
  UserEventModel,
} from '../services/contacts-api.models';
import type {Contact, ContactEvent, ContactGroup} from '../types/contacts.types';

export function toIdString(id: number): string {
  return String(id);
}

export function toIdNumber(id: string): number {
  return Number(id);
}

export function mapContactGroupModel(model: ContactGroupModel): ContactGroup {
  return {
    id: toIdString(model.id),
    name: model.name,
    contactsCount: model.contactsCount,
  };
}

export function mapContactGroupPage(result: PaginatedResult<ContactGroupModel>): {
  items: ContactGroup[];
  total: number;
} {
  return {
    items: result.items.map(mapContactGroupModel),
    total: result.totalItemsCount,
  };
}

export function mapContactListItemModel(
  model: ContactListItemModel,
  options?: {groupName?: string},
): Contact {
  return {
    id: toIdString(model.id),
    firstName: model.firstName,
    lastName: model.lastName,
    mobile: String(model.mobile),
    groups: options?.groupName ? [options.groupName] : [],
    events: [],
    eventIds: [],
    createdAt: '',
  };
}

export function mapContactsPage(
  result: PaginatedResult<ContactListItemModel>,
  options?: {groupName?: string},
): {items: Contact[]; total: number} {
  return {
    items: result.items.map(item => mapContactListItemModel(item, options)),
    total: result.totalItemsCount,
  };
}

export function formatTimestampDate(timestamp: number): string {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fa-IR').format(date);
}

export function mapContactDetailModel(
  model: ContactDetailModel,
  options: {
    groupName?: string;
    eventNameById: Map<string, string>;
  },
): Contact {
  const eventIds = model.userEvents?.map(e => toIdString(e.userEventId)) ?? [];
  const events = eventIds
    .map(id => options.eventNameById.get(id))
    .filter((name): name is string => !!name);

  return {
    id: toIdString(model.id),
    firstName: model.firstName,
    lastName: model.lastName,
    mobile: String(model.mobile),
    email: model.email || undefined,
    address: model.address || undefined,
    isMale: model.isMale,
    groups: options.groupName ? [options.groupName] : [],
    events,
    eventIds,
    createdAt: formatTimestampDate(model.createdAt),
  };
}

export function mapUserEventModel(model: UserEventModel): ContactEvent {
  return {
    id: toIdString(model.id),
    name: model.name,
    usedCount: model.usedCount,
    type: 'shared',
    eventDate: null,
  };
}

export function mapBulkPreviewModel(
  model: BulkPreviewModel,
): import('../types/contacts.types').BulkPreviewResult {
  return {
    wrongNumber: model.wrongNumber,
    totalCount: model.totalCount,
    systemScopeExistsContactCount: model.systemScopeExistsContactCount,
    groupScopeExistsContactCount: model.groupScopeExistsContactCount,
    addCount: model.addCount,
    key: model.key,
    distinctCount: model.distinctCount,
  };
}

export function unwrapApiResult<T>(response: ApiResponse<T>): T {
  if (!response.success) {
    throw new Error(response.description ?? 'API request failed');
  }
  return response.result;
}

export function jsonStringBody(value: string): string {
  return JSON.stringify(value);
}
